import { TopoMap, DisplayEdgeSet } from '../dtos/topology/TopoMap';
import { Point } from '../dtos/topology/Point';
import { IMapRenderer } from './IMapRenderer';
import { MouseOverLabel } from './MouseOverLabel';
import { DeviceNodeRenderer } from './DeviceNodeRenderer';
import { DeviceGroupNodeRenderer } from './DeviceGroupNodeRenderer';
import { HubNodeRenderer } from './HubNodeRenderer';
import { DeviceLinkRenderer } from './DeviceLinkRenderer';
import { CanvasExtensions } from './CanvasExtensions';
import { TopoImageLibrary } from './TopoImageLibrary';
import { Matrix } from './Matrix';
import { DeviceLink } from '../dtos/topology/DeviceLink';
import { DeviceNode } from '../dtos/topology/DeviceNode';
import { DeviceGroupNode } from '../dtos/topology/DeviceGroupNode';


export interface MapControlListener {
	onDeviceDoubleClick(deviceGuid: string): void;
	onDeviceGroupDoubleClick(deviceGroupGuid: string): void;
	onLinkDoubleClick(links: DeviceLink[]): void;
	onMapDoubleClick(): void;
	onMoveNode(nodeId: number, newX: number, newY: number): void;
	onContextMenu(targetType: string, targets: any[], ev: MouseEvent): void;

	// Server-operation stubs — fired when the user requests an op that needs a server round-trip.
	// The listener calls the appropriate web API and passes the updated map back via loadMap().
	onAddDevicesRequested?(): void;
	onRemoveDevicesRequested?(nodes: DeviceNode[]): void;
}

export interface MapControlSettings {
	isDarkTheme?: boolean;
	zoomMin?: number;
	zoomMax?: number;
	zoomMiddle?: number;
	zoomValue?: number;
}


export class MapControl {
	private map: TopoMap | null = null;
	private isEditable = false;
	private orderedRenderers: IMapRenderer[] = [];
	private mouseOverLabels: MouseOverLabel[] = [];
	private allLabels: MouseOverLabel[] = [];
	private visible = false;
	private w_centerPt: Point = { x: 0, y: 0 };

	/** Set to true to flip black link colours to white in dark-mode UIs. */
	public isDarkTheme: boolean;

	constructor(
		private canvas: HTMLCanvasElement,
		private overLay: HTMLCanvasElement,
		private container: HTMLDivElement,
		private listener: MapControlListener,
		settings?: MapControlSettings
	) {
		this.isDarkTheme = settings?.isDarkTheme ?? false;
		this._scale = settings?.zoomValue ?? 1;
		this.zoomMin = settings?.zoomMin ?? 0.1;
		this.zoomMax = settings?.zoomMax ?? 2;
		this.zoomMiddle = settings?.zoomMiddle ?? 1;
		this.w_centerPt = { x: 0, y: 0 };

		window.addEventListener('resize', () => this.onResize(), false);

		this.orderedRenderers = [
			new DeviceGroupNodeRenderer(listener),
			new DeviceNodeRenderer(listener),
			new HubNodeRenderer(listener),
			new DeviceLinkRenderer(listener),
		];

		this.initMouseListeners();
	}

	// ---- Map data ----

	public hasMap(): boolean {
		return !!this.map;
	}

	public getMap(): TopoMap | null {
		return this.map;
	}

	/** Load a new map. Alias: loadMap(). */
	public setMap(map: TopoMap, isEditable: boolean): void {
		this.map = map;
		this.w_centerPt = this.worldMidPoint;
		this.populateMapDisplayEdges(map);
		this.orderedRenderers.forEach(r => r.setMap(map));
		this.isEditable = isEditable;
		this.initAllLabels();
		this.drawMap();
		this.fitAndCenterWhenContainerReady();
	}

	/** Load a new map. */
	public loadMap(map: TopoMap, isEditable: boolean = false): void {
		this.setMap(map, isEditable);
	}

	public setVisible(value: boolean): void {
		this.visible = value;
		this.fitAndCenterWhenContainerReady();
	}

	// ---- Zoom ----

	public zoomChange = 0.05;
	public zoomMax: number;
	public zoomMin: number;
	public zoomMiddle: number;
	private _scale: number;

	public get scale(): number {
		return this._scale;
	}

	public get zoomValue(): number {
		return this._scale;
	}

	public set zoomValue(value: number) {
		this.zoomAtCenter(value);
	}

	public zoomValueChanged: ((value: number) => void) | null = null;

	public zoomIn(): void {
		this.zoomInAt(this.viewCenter);
	}

	public zoomOut(): void {
		this.zoomOutAt(this.viewCenter);
	}

	public zoomAtCenter(zoomValue: number): void {
		this.zoom(zoomValue, this.viewCenter);
	}

	public zoomFullSizeAtCenter(): void {
		this.zoomAtCenter(1);
	}

	/** Fit map to view and center. Also: scrollAndZoomToCenter(). */
	public fitAndCenter(): void {
		this.zoomToFit();
		this.scrollToCenter();
	}

	/** Fit map to view and center. */
	public scrollAndZoomToCenter(): void {
		this.fitAndCenter();
	}

	// ---- Navigation ----

	public showDeviceOnMap(deviceGuid: string): void {
		this.visible = true;
		this.clearAllSelections();
		let deviceNode = this.map?.deviceNodes.find(n => n.deviceGuid === deviceGuid);
		if (deviceNode) {
			deviceNode.selected = true;
			this.w_centerPt = { x: deviceNode.centerX, y: deviceNode.centerY };
			this.zoomFullSizeAtCenterWhenContainerReady();
		}
	}

	private clearAllSelections(): void {
		this.map?.deviceNodes.forEach(n => n.selected = false);
		this.map?.deviceGroupNodes.forEach(n => n.selected = false);
	}

	public showDeviceGroupOnMap(deviceGroupGuid: string): void {
		this.visible = true;
		let node = this.map?.deviceGroupNodes.find(n => n.deviceGroupGuid === deviceGroupGuid);
		if (node) {
			node.selected = true;
			this.w_centerPt = { x: node.centerX, y: node.centerY };
			this.zoomFullSizeAtCenterWhenContainerReady();
		}
	}

	// ---- Layer renderer (stub — extended in TopoMapControl) ----

	private _layerRenderer: string = 'Network';
	public get layerRenderer(): string { return this._layerRenderer; }
	public set layerRenderer(value: string) { this._layerRenderer = value; }

	// ---- Batch update ----

	private _updateDepth = 0;

	public beginUpdate(): void {
		this._updateDepth++;
	}

	public endUpdate(): void {
		if (this._updateDepth > 0) {
			this._updateDepth--;
			if (this._updateDepth === 0)
				this.drawMap();
		}
	}

	// ---- Render ----

	public redraw(): void {
		this.drawMap();
	}

	// ---- Coordinate transforms ----

	public viewToWorld = (point: Point): Point => {
		return this.createInverseTransformMatrix().TransformPoint(point);
	}

	public worldToView = (point: Point): Point => {
		return this.createTransformMatrix().TransformPoint(point);
	}

	public createInverseTransformMatrix(): Matrix {
		var m = new Matrix();
		m.Translate(this.w_centerPt.x, this.w_centerPt.y);
		m.Scale(1 / this._scale, 1 / this._scale);
		m.Translate(-this.containerWidth / 2, -this.containerHeight / 2);
		return m;
	}

	public createTransformMatrix(): Matrix {
		var m = new Matrix();
		m.Translate(this.containerWidth / 2, this.containerHeight / 2);
		m.Scale(this._scale, this._scale);
		m.Translate(-this.w_centerPt.x, -this.w_centerPt.y);
		return m;
	}

	// ---- Private helpers ----

	private get worldMidPoint(): Point {
		if (this.map) {
			return { x: this.map.left + this.map.width / 2, y: this.map.top + this.map.height / 2 };
		}
		return { x: 0, y: 0 };
	}

	private get viewCenter(): Point {
		return { x: this.containerWidth / 2, y: this.containerHeight / 2 };
	}

	private get containerWidth(): number { return this.container.clientWidth; }
	private get containerHeight(): number { return this.container.clientHeight; }

	private populateMapDisplayEdges(map: TopoMap): void {
		let dispEdges = new DisplayEdgeSet(map.drawSettings, this.isDarkTheme);
		map.deviceLinks.forEach((link: DeviceLink) => {
			link.linkEdges.forEach(edge => {
				let local = edge.localConnection;
				let remote = edge.remoteConnection;
				let dispEdge = dispEdges.get(local.start, remote.start);
				dispEdge.deviceLinkEdges.push(edge);
				dispEdge.deviceLinks.push(link);
			});
		});
		map.displayEdges = dispEdges;
	}

	private initAllLabels(): void {
		this.allLabels = [];
		if (this.map?.layoutSettings.showLinkLabels) {
			this.orderedRenderers.forEach(r => this.allLabels.push(...r.getAllLabels()));
		}
	}

	private zoom(newZoom: number, location: Point): void {
		newZoom = Math.max(this.zoomMin, Math.min(this.zoomMax, newZoom));
		var worldPoint = this.viewToWorld(location);
		this._scale = newZoom;
		this.scrollWorldPointToViewPoint(worldPoint, location);
		this.drawMap();
		if (this.zoomValueChanged) this.zoomValueChanged(newZoom);
	}

	private zoomInAt(location: Point): void { this.zoom(this._scale + this.zoomChange, location); }
	private zoomOutAt(location: Point): void { this.zoom(this._scale - this.zoomChange, location); }

	private zoomToFit(): void {
		var viewWidth = this.containerWidth;
		var viewHeight = this.containerHeight;
		var mapWidth = this.map?.width ?? 0;
		var mapHeight = this.map?.height ?? 0;
		if (mapWidth > 0 && mapHeight > 0) {
			var ratio = Math.min(viewWidth / mapWidth, viewHeight / mapHeight);
			var zoomValue = Math.min(this.zoomMiddle * ratio, this.zoomMiddle);
			this.zoomAtCenter(zoomValue);
		}
	}

	private scrollToCenter(): void {
		this.w_centerPt = this.worldMidPoint;
		this.drawMap();
	}

	private scrollWorldPointToViewPoint(worldPoint: Point, viewPoint: Point): void {
		let newWorldPoint = this.viewToWorld(viewPoint);
		this.w_centerPt.x -= newWorldPoint.x - worldPoint.x;
		this.w_centerPt.y -= newWorldPoint.y - worldPoint.y;
	}

	// Keyed flags for pending deferred operations (e.g. 'doing_fitAndCenter')
	private _pendingOps: { [key: string]: boolean } = {};

	private doOpWhenContainerReady(opName: string, op: () => void): void {
		let field = 'doing_' + opName;
		if (this.visible && !this._pendingOps[field]) {
			this._pendingOps[field] = true;
			this.doOpWhenContainerReady_Helper(field, op);
		}
	}

	private doOpWhenContainerReady_Helper(field: string, op: () => void): void {
		if (this.containerWidth > 0 && this.containerHeight > 0) {
			this._pendingOps[field] = false;
			if (this.map) op();
		} else {
			setTimeout(() => this.doOpWhenContainerReady_Helper(field, op), 100);
		}
	}

	private fitAndCenterWhenContainerReady(): void {
		this.doOpWhenContainerReady('fitAndCenter', () => this.fitAndCenter());
	}

	private zoomFullSizeAtCenterWhenContainerReady(): void {
		this.doOpWhenContainerReady('zoomFullSizeAtCenter', () => this.zoomFullSizeAtCenter());
	}

	// ---- Mouse / interaction ----

	private initMouseListeners(): void {
		this.canvas.onmousemove = this.onMouseMove;
		this.canvas.ondblclick = this.onDoubleClick;
		this.canvas.onmousedown = this.onMouseDown;
		this.canvas.onmouseup = this.onMouseUp;
		this.canvas.onmouseenter = this.onMouseEnter;
		this.canvas.onwheel = this.onMouseWheel;
		this.canvas.oncontextmenu = this.onContextMenu;
	}

	private adjustToCanvas(d_Pt: Point): Point {
		let rect = this.canvas.getBoundingClientRect();
		return { x: d_Pt.x - rect.left, y: d_Pt.y - rect.top };
	}

	private mouseDown: Point | null = null;
	private dragging = false;
	private dragRenderer: IMapRenderer | null = null;
	private static readonly DragBuffer = 5;
	private w_dragStartPt: Point | null = null;
	private w_dragStartCenterPt: Point | null = null;
	private dragTransform: Matrix | null = null;
	private dragTimer: any;
	private static readonly DragTimerThreshold = 0;
	private static readonly DragTimerTimeout = 200;
	private _rubberBandStart: Point | null = null;
	private _rubberBandEnd: Point | null = null;
	private _rubberBandQueued = false;
	private _pendingClickClear = false;
	private _autoScrollTimer: any = null;
	private _autoScrollVx = 0;
	private _autoScrollVy = 0;
	private _lastDragMousePt: Point | null = null;
	private static readonly AutoScrollZone = 40;    // px from edge that triggers scroll
	private static readonly AutoScrollSpeed = 8;    // max view-px per tick
	private static readonly AutoScrollInterval = 16; // ms (~60 fps)

	private clearDragTimer(): void {
		if (this.dragTimer) {
			clearTimeout(this.dragTimer);
			this.dragTimer = null;
		}
	}

	private onMouseWheel = (ev: WheelEvent) => {
		this.clearPopupTimer();
		if (ev.ctrlKey) {
			var location = this.adjustToCanvas({ x: ev.clientX, y: ev.clientY });
			if (ev.deltaY < 0) this.zoomInAt(location);
			else this.zoomOutAt(location);
			ev.preventDefault();
		}
	}

	private onDoubleClick = (ev: MouseEvent) => {
		this.clearPopupTimer();
		var worldPoint = this.viewToWorld(this.adjustToCanvas({ x: ev.clientX, y: ev.clientY }));
		if (!this.orderedRenderers.some(r => r.handleDoubleClick(worldPoint, this.scale))) {
			this.listener.onMapDoubleClick();
		}
	}

	private onMouseEnter = (ev: MouseEvent) => {
		if (this.mouseDown !== null && ev.buttons === 0) this.endDrag();
	}

	private onMouseDown = (ev: MouseEvent) => {
		this.mouseDown = this.adjustToCanvas({ x: ev.clientX, y: ev.clientY });

		if (ev.button === 0) {
			let point = this.viewToWorld(this.mouseDown);
			let isSelect = ev.shiftKey || ev.ctrlKey;
			let hitSomething = false;

			if (this.isEditable) {
				this.dragRenderer = null;
				this.orderedRenderers.some(r => {
					if (r.handleClick(point, this.scale, isSelect)) {
						this.dragRenderer = r;
						hitSomething = true;
						return true;
					}
					return false;
				});
				if (isSelect) this.drawMap();
			}

			if (!hitSomething) {
				if (isSelect && this.isEditable) {
					// Shift/Ctrl + drag on empty space will start rubber-band selection
					this._rubberBandQueued = true;
				} else if (!isSelect) {
					// Plain click on empty canvas — defer selection clear to mouse-up
					this._pendingClickClear = true;
				}
			}
		}

		ev.preventDefault();
	}

	private onMouseUp = (ev: MouseEvent) => {
		this.endDrag();
		ev.preventDefault();
	}

	private endDrag(): void {
		if (this.dragging) {
			if (this._rubberBandStart) {
				this._selectNodesInRubberBand(this._rubberBandStart, this._rubberBandEnd ?? this._rubberBandStart);
			} else if (this.dragRenderer) {
				this.dragRenderer.handleUp();
			}
			this.clearDragTimer();
			this.drawMap();
		} else if (this._pendingClickClear) {
			this.clearAllSelections();
			this.drawMap();
		}
		this._clearAutoScrollTimer();
		this.mouseDown = null;
		this.dragging = false;
		this._pendingClickClear = false;
		this._rubberBandQueued = false;
		this._rubberBandStart = null;
		this._rubberBandEnd = null;
		this._lastDragMousePt = null;
		this.w_dragStartPt = null;
		this.w_dragStartCenterPt = null;
		this.dragTransform = null;
		this.dragRenderer = null;
	}

	private popupTimer: any;

	private onMouseMove = (ev: MouseEvent) => {
		this.clearPopupTimer();

		var mouseLocation = this.adjustToCanvas({ x: ev.clientX, y: ev.clientY });
		if (this.dragging) {
			this.drag(mouseLocation);
		} else if (this.isDragStart(this.mouseDown, mouseLocation)) {
			this.dragging = true;
			this._pendingClickClear = false;
			this.dragTransform = new Matrix();
			this.dragTransform.Translate(this.w_centerPt.x, this.w_centerPt.y);
			this.dragTransform.Scale(1 / this._scale, 1 / this._scale);
			this.dragTransform.Translate(-this.containerWidth / 2, -this.containerHeight / 2);

			if (!this.dragRenderer) {
				if (this._rubberBandQueued) {
					// Shift/Ctrl + drag on empty space — rubber-band selection
					this._rubberBandStart = this.mouseDown;
				} else {
					// Plain drag on empty space — pan (both editable and view-only)
					this.w_dragStartPt = this.dragTransform.TransformPoint(mouseLocation);
					this.w_dragStartCenterPt = { x: this.w_centerPt.x, y: this.w_centerPt.y };
				}
			}

			let drawStart = performance.now();
			this.drawMap();
			let drawEnd = performance.now();
			if ((drawEnd - drawStart) >= MapControl.DragTimerThreshold) {
				this.dragTimer = setInterval(() => this.drawMap(), MapControl.DragTimerTimeout);
			}
			this.drag(mouseLocation);
		}

		var worldPoint = this.viewToWorld(mouseLocation);
		this.mouseOverLabels = [];
		this.orderedRenderers.forEach(r => {
			this.mouseOverLabels.push(...r.getMouseOverLabels(worldPoint, this.scale));
		});
		this.drawPopups();
		this.popupTimer = setTimeout(this.handleHover, 700, mouseLocation);

		ev.preventDefault();
	}

	private isDragStart(mouseDown: Point | null, mouseLocation: Point): boolean {
		if (mouseDown === null) return false;
		return Math.abs(mouseLocation.x - mouseDown.x) > MapControl.DragBuffer ||
		       Math.abs(mouseLocation.y - mouseDown.y) > MapControl.DragBuffer;
	}

	private drag(mouseLocation: Point): void {
		if (this._rubberBandStart) {
			this._rubberBandEnd = mouseLocation;
			this.drawPopups();
			return;
		}
		if (this.dragRenderer) {
			// Use the current inverse transform so auto-scroll panning is accounted for
			this.dragRenderer.handleMove(this.viewToWorld(mouseLocation), this.scale);
			this._lastDragMousePt = mouseLocation;
			this._updateAutoScroll(mouseLocation);
		} else {
			// Pan — dragTransform captures the center at drag start; use it for stable pan math
			let w_Pt = this.dragTransform!.TransformPoint(mouseLocation);
			this.w_centerPt.x = this.w_dragStartCenterPt!.x - (w_Pt.x - this.w_dragStartPt!.x);
			this.w_centerPt.y = this.w_dragStartCenterPt!.y - (w_Pt.y - this.w_dragStartPt!.y);
		}
		if (!this.dragTimer) this.drawMap();
	}

	private _updateAutoScroll(mouseLocation: Point): void {
		const zone = MapControl.AutoScrollZone;
		const speed = MapControl.AutoScrollSpeed;
		const w = this.containerWidth;
		const h = this.containerHeight;
		// Velocity scales linearly from 0 at zone edge to AutoScrollSpeed at canvas edge
		let vx = 0, vy = 0;
		if (mouseLocation.x < zone)       vx = -(zone - mouseLocation.x) / zone * speed;
		else if (mouseLocation.x > w - zone) vx =  (mouseLocation.x - (w - zone)) / zone * speed;
		if (mouseLocation.y < zone)       vy = -(zone - mouseLocation.y) / zone * speed;
		else if (mouseLocation.y > h - zone) vy =  (mouseLocation.y - (h - zone)) / zone * speed;
		this._autoScrollVx = vx;
		this._autoScrollVy = vy;
		if (vx !== 0 || vy !== 0) {
			if (!this._autoScrollTimer) {
				this._autoScrollTimer = setInterval(() => this._doAutoScroll(), MapControl.AutoScrollInterval);
			}
		} else {
			this._clearAutoScrollTimer();
		}
	}

	private _doAutoScroll(): void {
		if (!this.dragRenderer || !this._lastDragMousePt) {
			this._clearAutoScrollTimer();
			return;
		}
		this.w_centerPt.x += this._autoScrollVx / this._scale;
		this.w_centerPt.y += this._autoScrollVy / this._scale;
		this.dragRenderer.handleMove(this.viewToWorld(this._lastDragMousePt), this._scale);
		this.drawMap();
	}

	private _clearAutoScrollTimer(): void {
		if (this._autoScrollTimer) {
			clearInterval(this._autoScrollTimer);
			this._autoScrollTimer = null;
		}
	}

	private handleHover = (mouseLocation: Point) => {
		if (this._scale < 0.1) return;
		var worldPoint = this.viewToWorld(mouseLocation);
		this.orderedRenderers.some(r => r.handleHover(worldPoint, mouseLocation));
	}

	private onContextMenu = (ev: MouseEvent) => {
		this.clearPopupTimer();
		var worldPoint = this.viewToWorld(this.adjustToCanvas({ x: ev.clientX, y: ev.clientY }));
		if (!this.orderedRenderers.some(r => r.handleContextMenu(worldPoint, this.scale, ev))) {
			this.listener.onContextMenu('map', [], ev);
		}
		ev.preventDefault();
	}

	private clearPopupTimer(): void {
		if (this.popupTimer) {
			clearTimeout(this.popupTimer);
			this.popupTimer = null;
		}
	}

	private onResize(): void { this.drawMap(); }

	// ---- Drawing ----

	private drawMap(): void {
		if (!TopoImageLibrary.isLoaded) return;
		if (this._updateDepth > 0) return;

		if (this.canvas && this.map) {
			this.canvas.width = this.containerWidth;
			this.canvas.height = this.containerHeight;

			var ctx = this.canvas.getContext('2d')!;
			ctx.translate(this.containerWidth / 2, this.containerHeight / 2);
			ctx.scale(this._scale, this._scale);
			ctx.translate(-this.w_centerPt.x, -this.w_centerPt.y);

			for (var i = this.orderedRenderers.length - 1; i >= 0; i--) {
				this.orderedRenderers[i].render(ctx);
			}

			if (this.map.layoutSettings.showLinkLabels) {
				this.drawAllLabels(ctx);
			}
		}
	}

	private drawPopups(): void {
		var viewRect = this.container.getBoundingClientRect();
		this.overLay.width = viewRect.width;
		this.overLay.height = viewRect.height;
		var ctx = this.overLay.getContext('2d')!;

		// Rubber-band rectangle (view space — no world transform)
		if (this._rubberBandStart && this._rubberBandEnd) {
			this._drawRubberBand(ctx, this._rubberBandStart, this._rubberBandEnd);
		}

		// Mouse-over labels (world space)
		if (this._scale >= 0.3) {
			ctx.save();
			ctx.translate(this.containerWidth / 2, this.containerHeight / 2);
			ctx.scale(this._scale, this._scale);
			ctx.translate(-this.w_centerPt.x, -this.w_centerPt.y);
			this.drawLabels(ctx, this.mouseOverLabels, '#FFFFFF');
			ctx.restore();
		}
	}

	private _drawRubberBand(ctx: CanvasRenderingContext2D, start: Point, end: Point): void {
		const x = Math.min(start.x, end.x);
		const y = Math.min(start.y, end.y);
		const w = Math.abs(end.x - start.x);
		const h = Math.abs(end.y - start.y);
		ctx.save();
		ctx.fillStyle = 'rgba(0, 120, 215, 0.15)';
		ctx.fillRect(x, y, w, h);
		ctx.strokeStyle = 'rgba(0, 120, 215, 0.8)';
		ctx.lineWidth = 1;
		ctx.setLineDash([4, 2]);
		ctx.strokeRect(x, y, w, h);
		ctx.restore();
	}

	private _selectNodesInRubberBand(start: Point, end: Point): void {
		const w_topLeft = this.viewToWorld({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y) });
		const w_bottomRight = this.viewToWorld({ x: Math.max(start.x, end.x), y: Math.max(start.y, end.y) });
		this.clearAllSelections();
		this.map?.deviceNodes.forEach(node => {
			if (node.x >= w_topLeft.x && node.y >= w_topLeft.y &&
				node.x + node.width <= w_bottomRight.x &&
				node.y + node.height <= w_bottomRight.y) {
				node.selected = true;
			}
		});
		this.map?.deviceGroupNodes.forEach(node => {
			if (node.x >= w_topLeft.x && node.y >= w_topLeft.y &&
				node.x + node.width <= w_bottomRight.x &&
				node.y + node.height <= w_bottomRight.y) {
				node.selected = true;
			}
		});
	}

	private drawAllLabels(ctx: CanvasRenderingContext2D): void {
		this.drawLabels(ctx, this.allLabels);
	}

	private drawLabels(ctx: CanvasRenderingContext2D, labels: MouseOverLabel[], backgroundColor: string | null = null): void {
		let combined: { location: Point; labels: string[] }[] = [];

		labels.forEach(label => {
			let existing = combined.find(v => v.location.x === label.location.x && v.location.y === label.location.y);
			if (existing) {
				existing.labels.push(...label.labels);
			} else {
				combined.push({ location: label.location, labels: [...label.labels] });
			}
		});

		combined.forEach(value => {
			let display = value.labels
				.map(l => l.trim())
				.filter(l => !!l)
				.join(', ') || ' ';
			CanvasExtensions.drawLabel(ctx, value.location.x, value.location.y, display, backgroundColor ?? undefined);
		});
	}
}
